// TanStack Query hooks for the Persons registry module. A Person is a central
// managed identity (the row personnel / client contacts / principals / users
// link to). Heavy PII is split across sensitivity tiers server-side (spec
// section 2.1); list/detail return masked government-ID rows (type + last4) and
// the full number is only ever fetched via the dedicated reveal mutation.
// Mirrors packages/api/src/routes/persons.ts.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreatePersonAddressInput,
  CreatePersonEmailInput,
  CreatePersonEmergencyContactInput,
  CreatePersonInput,
  CreatePersonPhoneInput,
  GovernmentIdRevealResponse,
  Person,
  PersonAddress,
  PersonAddressResponse,
  PersonAddressesListResponse,
  PersonDetail,
  PersonEmail,
  PersonEmailResponse,
  PersonEmailsListResponse,
  PersonEmergencyContact,
  PersonEmergencyContactResponse,
  PersonEmergencyContactsListResponse,
  PersonGovernmentId,
  PersonGovernmentIdResponse,
  PersonGovernmentIdsListResponse,
  PersonHistoryResponse,
  PersonPhone,
  PersonPhoneResponse,
  PersonPhonesListResponse,
  PersonPhotoResponse,
  PersonResponse,
  PersonsListResponse,
  SetPersonGovernmentIdInput,
  UpdatePersonAddressInput,
  UpdatePersonEmailInput,
  UpdatePersonEmergencyContactInput,
  UpdatePersonGovernmentIdInput,
  UpdatePersonInput,
  UpdatePersonPhoneInput,
} from '@dsr/shared-types';
import { api, uploadFile } from '../client';

// ---------- Query keys ----------

const KEY = 'persons';

export const personsKeys = {
  all: [KEY] as const,
  lists: () => [KEY, 'list'] as const,
  list: (filters: PersonsFilters) => [KEY, 'list', { ...filters }] as const,
  detail: (id: number) => [KEY, 'detail', id] as const,
  // Nested satellite collections hang off the person detail subtree so a
  // single invalidate of detail(id) refreshes every tab for that person.
  sub: (personId: number, seg: string) => [KEY, 'detail', personId, seg] as const,
  history: (id: number) => [KEY, 'detail', id, 'history'] as const,
};

// =============================================================================
// Person (core)
// =============================================================================

export interface PersonsFilters {
  q?: string;
  limit?: number;
  offset?: number;
}

export function usePersons(filters: PersonsFilters = {}) {
  const params = new URLSearchParams();
  if (filters.q && filters.q.trim() !== '') params.set('q', filters.q.trim());
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.offset !== undefined) params.set('offset', String(filters.offset));
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/persons?${qs}` : '/api/persons';
  return useQuery({
    queryKey: personsKeys.list(filters),
    queryFn: ({ signal }) => api.get<PersonsListResponse>(url, signal),
  });
}

export function usePerson(id: number | null) {
  return useQuery({
    queryKey: personsKeys.detail(id ?? 0),
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) => api.get<PersonResponse>(`/api/persons/${id}`, signal),
  });
}

export function useCreatePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePersonInput) =>
      api.post<PersonResponse>('/api/persons', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: personsKeys.lists() });
    },
  });
}

export interface UpdatePersonArgs extends UpdatePersonInput {
  id: number;
}

export function useUpdatePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdatePersonArgs) =>
      api.patch<PersonResponse>(`/api/persons/${id}`, patch),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: personsKeys.lists() });
      void qc.invalidateQueries({ queryKey: personsKeys.detail(id) });
    },
  });
}

export function useDeletePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/persons/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: personsKeys.lists() });
    },
  });
}

// ---------- Photo (read: person.read / write: person.write) ----------
//
// The image itself is fetched as an authenticated blob inside PersonAvatar
// (fetchObjectUrl); these mutations only manage the bytes. On success they
// invalidate the person detail + list so the refreshed photo_path re-triggers
// the avatar fetch.

export function useUploadPersonPhoto() {
  const qc = useQueryClient();
  return useMutation<PersonPhotoResponse, Error, { personId: number; file: File }>({
    mutationFn: ({ personId, file }) =>
      uploadFile<PersonPhotoResponse>(`/api/persons/${personId}/photo`, file),
    onSuccess: (_data, { personId }) => {
      void qc.invalidateQueries({ queryKey: personsKeys.detail(personId) });
      void qc.invalidateQueries({ queryKey: personsKeys.lists() });
    },
  });
}

export function useDeletePersonPhoto() {
  const qc = useQueryClient();
  return useMutation<void, Error, { personId: number }>({
    mutationFn: ({ personId }) => api.delete<void>(`/api/persons/${personId}/photo`),
    onSuccess: (_data, { personId }) => {
      void qc.invalidateQueries({ queryKey: personsKeys.detail(personId) });
      void qc.invalidateQueries({ queryKey: personsKeys.lists() });
    },
  });
}

// =============================================================================
// Nested satellite sub-resources
// =============================================================================
//
// addresses / phones / emails / emergency-contacts share an identical CRUD
// shape, differing only in URL segment, response wrapper key, and types:
//   GET    /api/persons/:id/<seg>
//   POST   /api/persons/:id/<seg>
//   PATCH  /api/persons/:id/<seg>/:satId
//   DELETE /api/persons/:id/<seg>/:satId
// A small factory produces the four hooks per category. Government IDs are a
// separate block (extra reveal endpoint + distinct write permission).

interface SubResourceShape<List, Item, Create, Update> {
  /** URL segment, e.g. 'addresses'. */
  segment: string;
  /** Phantom markers so TS infers the four type params from the call site. */
  _list?: List;
  _item?: Item;
  _create?: Create;
  _update?: Update;
}

interface SubResourceHooks<List, Item, Create, Update> {
  useList: (personId: number | null, enabled?: boolean) => ReturnType<typeof useQuery<List>>;
  useCreate: () => ReturnType<typeof useMutation<Item, Error, { personId: number; input: Create }>>;
  useUpdate: () => ReturnType<
    typeof useMutation<Item, Error, { personId: number; satId: number; patch: Update }>
  >;
  useDelete: () => ReturnType<typeof useMutation<void, Error, { personId: number; satId: number }>>;
}

function makeSubResource<List, Item, Create, Update>(
  cfg: SubResourceShape<List, Item, Create, Update>,
): SubResourceHooks<List, Item, Create, Update> {
  const { segment } = cfg;

  function useList(personId: number | null, enabled = true) {
    return useQuery<List>({
      queryKey: personsKeys.sub(personId ?? 0, segment),
      enabled: enabled && personId !== null && personId > 0,
      queryFn: ({ signal }) =>
        api.get<List>(`/api/persons/${personId}/${segment}`, signal),
    });
  }

  function useCreate() {
    const qc = useQueryClient();
    return useMutation<Item, Error, { personId: number; input: Create }>({
      mutationFn: ({ personId, input }) =>
        api.post<Item>(`/api/persons/${personId}/${segment}`, input),
      onSuccess: (_data, { personId }) => {
        void qc.invalidateQueries({ queryKey: personsKeys.sub(personId, segment) });
        void qc.invalidateQueries({ queryKey: personsKeys.detail(personId) });
      },
    });
  }

  function useUpdate() {
    const qc = useQueryClient();
    return useMutation<Item, Error, { personId: number; satId: number; patch: Update }>({
      mutationFn: ({ personId, satId, patch }) =>
        api.patch<Item>(`/api/persons/${personId}/${segment}/${satId}`, patch),
      onSuccess: (_data, { personId }) => {
        void qc.invalidateQueries({ queryKey: personsKeys.sub(personId, segment) });
        void qc.invalidateQueries({ queryKey: personsKeys.detail(personId) });
      },
    });
  }

  function useDelete() {
    const qc = useQueryClient();
    return useMutation<void, Error, { personId: number; satId: number }>({
      mutationFn: ({ personId, satId }) =>
        api.delete<void>(`/api/persons/${personId}/${segment}/${satId}`),
      onSuccess: (_data, { personId }) => {
        void qc.invalidateQueries({ queryKey: personsKeys.sub(personId, segment) });
        void qc.invalidateQueries({ queryKey: personsKeys.detail(personId) });
      },
    });
  }

  return { useList, useCreate, useUpdate, useDelete };
}

// ---------- Addresses (read: person.read_pii / write: person.write) ----------

const addresses = makeSubResource<
  PersonAddressesListResponse,
  PersonAddressResponse,
  CreatePersonAddressInput,
  UpdatePersonAddressInput
>({ segment: 'addresses' });

export const usePersonAddresses = addresses.useList;
export const useCreatePersonAddress = addresses.useCreate;
export const useUpdatePersonAddress = addresses.useUpdate;
export const useDeletePersonAddress = addresses.useDelete;

// ---------- Phones (read: person.read / write: person.write) ----------

const phones = makeSubResource<
  PersonPhonesListResponse,
  PersonPhoneResponse,
  CreatePersonPhoneInput,
  UpdatePersonPhoneInput
>({ segment: 'phones' });

export const usePersonPhones = phones.useList;
export const useCreatePersonPhone = phones.useCreate;
export const useUpdatePersonPhone = phones.useUpdate;
export const useDeletePersonPhone = phones.useDelete;

// ---------- Emails (read: person.read / write: person.write) ----------

const emails = makeSubResource<
  PersonEmailsListResponse,
  PersonEmailResponse,
  CreatePersonEmailInput,
  UpdatePersonEmailInput
>({ segment: 'emails' });

export const usePersonEmails = emails.useList;
export const useCreatePersonEmail = emails.useCreate;
export const useUpdatePersonEmail = emails.useUpdate;
export const useDeletePersonEmail = emails.useDelete;

// ---------- Emergency contacts (read: person.read_pii / write: person.write) ----------

const emergencyContacts = makeSubResource<
  PersonEmergencyContactsListResponse,
  PersonEmergencyContactResponse,
  CreatePersonEmergencyContactInput,
  UpdatePersonEmergencyContactInput
>({ segment: 'emergency-contacts' });

export const usePersonEmergencyContacts = emergencyContacts.useList;
export const useCreatePersonEmergencyContact = emergencyContacts.useCreate;
export const useUpdatePersonEmergencyContact = emergencyContacts.useUpdate;
export const useDeletePersonEmergencyContact = emergencyContacts.useDelete;

// =============================================================================
// Government IDs (SENSITIVE)
//   GET list   - person.read_pii (masked rows: type + last4)
//   POST/PATCH/DELETE - person.write_government_ids (number encrypted at rest)
//   POST reveal- person.read_government_ids (decrypted number, on demand)
// =============================================================================

const GOV_ID_SEG = 'government-ids';

export function usePersonGovernmentIds(personId: number | null, enabled = true) {
  return useQuery({
    queryKey: personsKeys.sub(personId ?? 0, GOV_ID_SEG),
    enabled: enabled && personId !== null && personId > 0,
    queryFn: ({ signal }) =>
      api.get<PersonGovernmentIdsListResponse>(
        `/api/persons/${personId}/${GOV_ID_SEG}`,
        signal,
      ),
  });
}

export function useCreatePersonGovernmentId() {
  const qc = useQueryClient();
  return useMutation<
    PersonGovernmentIdResponse,
    Error,
    { personId: number; input: SetPersonGovernmentIdInput }
  >({
    mutationFn: ({ personId, input }) =>
      api.post<PersonGovernmentIdResponse>(`/api/persons/${personId}/${GOV_ID_SEG}`, input),
    onSuccess: (_data, { personId }) => {
      void qc.invalidateQueries({ queryKey: personsKeys.sub(personId, GOV_ID_SEG) });
      void qc.invalidateQueries({ queryKey: personsKeys.detail(personId) });
    },
  });
}

export function useUpdatePersonGovernmentId() {
  const qc = useQueryClient();
  return useMutation<
    PersonGovernmentIdResponse,
    Error,
    { personId: number; govId: number; patch: UpdatePersonGovernmentIdInput }
  >({
    mutationFn: ({ personId, govId, patch }) =>
      api.patch<PersonGovernmentIdResponse>(
        `/api/persons/${personId}/${GOV_ID_SEG}/${govId}`,
        patch,
      ),
    onSuccess: (_data, { personId }) => {
      void qc.invalidateQueries({ queryKey: personsKeys.sub(personId, GOV_ID_SEG) });
      void qc.invalidateQueries({ queryKey: personsKeys.detail(personId) });
    },
  });
}

export function useDeletePersonGovernmentId() {
  const qc = useQueryClient();
  return useMutation<void, Error, { personId: number; govId: number }>({
    mutationFn: ({ personId, govId }) =>
      api.delete<void>(`/api/persons/${personId}/${GOV_ID_SEG}/${govId}`),
    onSuccess: (_data, { personId }) => {
      void qc.invalidateQueries({ queryKey: personsKeys.sub(personId, GOV_ID_SEG) });
      void qc.invalidateQueries({ queryKey: personsKeys.detail(personId) });
    },
  });
}

// On-demand decrypt - not cached (sensitive); a one-shot mutation the UI calls
// when the user explicitly clicks reveal.
export function useRevealPersonGovernmentId() {
  return useMutation<
    GovernmentIdRevealResponse,
    Error,
    { personId: number; govId: number }
  >({
    mutationFn: ({ personId, govId }) =>
      api.post<GovernmentIdRevealResponse>(
        `/api/persons/${personId}/${GOV_ID_SEG}/${govId}/reveal`,
      ),
  });
}

// =============================================================================
// Change history (person.read_history)
// =============================================================================

export function usePersonHistory(personId: number | null, enabled = true) {
  return useQuery({
    queryKey: personsKeys.history(personId ?? 0),
    enabled: enabled && personId !== null && personId > 0,
    queryFn: ({ signal }) =>
      api.get<PersonHistoryResponse>(`/api/persons/${personId}/history`, signal),
  });
}

// Re-export the wire types pages commonly need alongside the hooks.
export type {
  Person,
  PersonAddress,
  PersonDetail,
  PersonEmail,
  PersonEmergencyContact,
  PersonGovernmentId,
  PersonPhone,
};
